import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getRoutes, insertRouteTrip, getLatestRouteTrip } from '../../APIs';
import { StoreContext } from '../../Library/contexts';
import { Spinner, Button } from '@chakra-ui/react';
import PATHS from '../../Routes/paths';
import { RideIcon } from '../../Assets/images/svgs';
import PinInputField from './PinInputField';
import './style.css';

const PIN_THRESHOLD_MS = 15 * 60 * 1000; // 15 minutes
const ROUTE_TRIP_CREATED = 1;

// ─── Switch Driver Modal ──────────────────────────────────────────────────────

const SwitchDriverModal = ({ onClose, onSwitched }) => {
  const { setActiveDriver, setDriverPinEnteredAt } = useContext(StoreContext);

  const handleVerified = useCallback((driver) => {
    setActiveDriver(driver);
    setDriverPinEnteredAt(Date.now());
    onSwitched(driver);
  }, [setActiveDriver, setDriverPinEnteredAt, onSwitched]);

  return (
    <div className="df-pin-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="df-pin-modal-card">
        <button className="df-pin-close-btn" onClick={onClose} aria-label="Close">✕</button>
        <PinInputField
          onVerified={handleVerified}
          title="Switch Driver"
          subtitle="Enter the new driver's 4-digit PIN."
        />
      </div>
    </div>
  );
};

// ─── PIN Modal (trip start) ───────────────────────────────────────────────────

const PinModal = ({ vehicle, route, routes, onClose, onSuccess }) => {
  const { setActiveRouteTrip, setActiveDriver, setDriverPinEnteredAt, userVehicles } = useContext(StoreContext);
  const navigate = useNavigate();
  const [ongoingTrip, setOngoingTrip] = useState(null);
  const [ongoingDriver, setOngoingDriver] = useState(null);
  const [loading, setLoading] = useState(false);

  // After driver is identified, check for an ongoing trip before inserting a new one
  const proceedWithDriver = useCallback((driver) => {
    if (setActiveDriver) setActiveDriver(driver);
    if (setDriverPinEnteredAt) setDriverPinEnteredAt(Date.now());

    setLoading(true);
    getLatestRouteTrip({ driverId: driver.idx }, (err, tripData) => {
      const hasOngoing =
        !err &&
        tripData &&
        tripData.status === ROUTE_TRIP_CREATED &&
        !tripData.endTs;

      if (hasOngoing) {
        setLoading(false);
        setOngoingTrip(tripData);
        setOngoingDriver(driver);
        return;
      }

      insertRouteTrip(
        { routeId: route.idx, vid: vehicle.idx || vehicle.vid, driverId: driver.idx },
        (tripErr, newTrip) => {
          setLoading(false);
          if (!tripErr && newTrip) {
            if (setActiveRouteTrip) setActiveRouteTrip(newTrip);
            onSuccess({ vehicle, route, tripId: newTrip.idx, driverId: driver.idx });
          }
        }
      );
    });
  }, [vehicle, route, setActiveDriver, setDriverPinEnteredAt, setActiveRouteTrip, onSuccess]);

  const handleGoToActiveTrip = () => {
    const trip = ongoingTrip;
    const driver = ongoingDriver;
    const allRoutes = routes || [];
    const foundRoute = allRoutes.find(r => String(r.idx) === String(trip.routeId));
    const foundVehicle = (userVehicles || []).find(
      v => String(v.idx) === String(trip.vid) || String(v.vid) === String(trip.vid)
    );
    navigate(`../${PATHS.NAVIGATION}`, {
      state: {
        vehicle: foundVehicle || vehicle,
        route: foundRoute || route,
        tripId: trip.idx,
        driverId: driver.idx,
      },
    });
  };

  if (ongoingTrip) {
    return (
      <div className="df-pin-overlay">
        <div className="df-pin-modal-card">
          <p className="df-pin-title df-pin-title--error">Trip Already in Progress</p>
          <p className="df-pin-subtitle df-pin-subtitle--centered">
            You already have an active trip. Complete or cancel it before starting a new one.
          </p>
          <Button className="df-pin-verify-btn" isLoading={loading} onClick={handleGoToActiveTrip}>
            GO TO ACTIVE TRIP
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="df-pin-overlay" onClick={(e) => e.target === e.currentTarget && !loading && onClose()}>
      <div className="df-pin-modal-card">
        <button className="df-pin-close-btn" onClick={onClose} aria-label="Close" disabled={loading}>✕</button>
        {loading ? (
          <div className="df-loader"><Spinner color="var(--ButtonPrimary)" /></div>
        ) : (
          <PinInputField onVerified={proceedWithDriver} />
        )}
      </div>
    </div>
  );
};

// ─── Select Route Page ────────────────────────────────────────────────────────

const SelectRoute = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const {
    setActiveRouteTrip,
    activeDriver,
    driverPinEnteredAt,
    setActiveDriver,
    setDriverPinEnteredAt,
    userVehicles,
  } = useContext(StoreContext);

  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [showPin, setShowPin] = useState(false);
  const [showSwitchDriver, setShowSwitchDriver] = useState(false);
  const [ongoingTripSkip, setOngoingTripSkip] = useState(null);
  const [skipCheckLoading, setSkipCheckLoading] = useState(false);
  const selectedVehicle = state?.vehicle;

  useEffect(() => {
    if (!selectedVehicle) { navigate(`../${PATHS.SELECT_BUS}`); return; }
    setLoading(true);
    getRoutes({ driver: true }, (err, data) => {
      setLoading(false);
      if (!err && data) setRoutes(data.filter(r => !r.archived));
    });
  }, []);

  const handleSuccess = ({ vehicle, route, tripId, driverId }) => {
    setShowPin(false);
    navigate(`../${PATHS.NAVIGATION}`, { state: { vehicle, route, tripId, driverId } });
  };

  const isWithin15Min = () =>
    driverPinEnteredAt && (Date.now() - driverPinEnteredAt) < PIN_THRESHOLD_MS;

  const handleContinue = () => {
    if (!selectedRoute) return;

    if (activeDriver && isWithin15Min()) {
      setSkipCheckLoading(true);
      getLatestRouteTrip({ driverId: activeDriver.idx }, (err, tripData) => {
        const hasOngoing =
          !err &&
          tripData &&
          tripData.status === ROUTE_TRIP_CREATED &&
          !tripData.endTs;

        if (hasOngoing) {
          setSkipCheckLoading(false);
          setOngoingTripSkip(tripData);
          return;
        }

        insertRouteTrip(
          { routeId: selectedRoute.idx, vid: selectedVehicle.idx || selectedVehicle.vid, driverId: activeDriver.idx },
          (tripErr, newTrip) => {
            setSkipCheckLoading(false);
            if (!tripErr && newTrip) {
              if (setActiveRouteTrip) setActiveRouteTrip(newTrip);
              navigate(`../${PATHS.NAVIGATION}`, {
                state: { vehicle: selectedVehicle, route: selectedRoute, tripId: newTrip.idx, driverId: activeDriver.idx },
              });
            }
          }
        );
      });
    } else {
      setShowPin(true);
    }
  };

  const handleGoToOngoingTrip = () => {
    const trip = ongoingTripSkip;
    const foundRoute = routes.find(r => String(r.idx) === String(trip.routeId));
    const foundVehicle = (userVehicles || []).find(
      v => String(v.idx) === String(trip.vid) || String(v.vid) === String(trip.vid)
    );
    navigate(`../${PATHS.NAVIGATION}`, {
      state: {
        vehicle: foundVehicle || selectedVehicle,
        route: foundRoute || selectedRoute,
        tripId: trip.idx,
        driverId: activeDriver.idx,
      },
    });
  };

  const handleDriverSwitched = (driver) => {
    setShowSwitchDriver(false);
  };

  return (
    <div className="df-page df-select-route">
      <div className="merged-header-background">
        {/* Logged-in driver banner */}
        {activeDriver && isWithin15Min() && (
          <div className="df-logged-in-banner">
            <span className="df-logged-in-label">Logged in as <strong>{activeDriver.nm}</strong></span>
            <button className="df-switch-driver-btn" onClick={() => setShowSwitchDriver(true)}>
              Switch Driver
            </button>
          </div>
        )}

        <div className="df-breadcrumbs">
          <span className="df-breadcrumb-item active" onClick={() => navigate(`../${PATHS.SELECT_BUS}`)}>
            Bus Selection
          </span>
          <span className="df-breadcrumb-separator">/</span>
          <span className="df-breadcrumb-item">Route Selection</span>
        </div>

        <div className="df-section-title-start">Selected Bus</div>

        <div className="df-selected-veh-header">
          <div className="df-vehicle-icon-box small">
            <img src="https://img.ajjas.com/images/web_assets/HEAVY_VEH_NORMAL.png" alt="Bus" />
          </div>
          <div className="df-vehicle-info">
            <span className="df-veh-reg large">{selectedVehicle?.reg}</span>
            <span className="df-separator">|</span>
            <span className="df-veh-type">{selectedVehicle?.model || "Big Bus"}</span>
            <span className="df-separator">|</span>
            <span className="df-veh-type">{selectedVehicle?.fuelType || "Petrol"}</span>
          </div>
        </div>
      </div>

      <div className="df-header center">
        <h2>Select a Route</h2>
        <p>Select a route you have to go on</p>
      </div>

      <div className="df-list-container select-route scrollable">
        {loading ? (
          <div className="df-loader"><Spinner color="var(--ButtonPrimary)" /></div>
        ) : (
          routes.map(route => (
            <div
              key={route.idx}
              className={`df-route-card${selectedRoute?.idx === route.idx ? ' selected' : ''}`}
              onClick={() => setSelectedRoute(route)}
            >
              <div className="df-route-icon">
                <span className="route-symbol"><RideIcon /></span>
              </div>
              <div className="df-route-name">{route.nm}</div>
            </div>
          ))
        )}
      </div>

      <div className="df-footer-btn-container">
        <Button
          className="df-action-btn"
          isDisabled={!selectedRoute || loading}
          isLoading={skipCheckLoading}
          loadingText="Checking…"
          onClick={handleContinue}
        >
          Continue
        </Button>
      </div>

      {showPin && (
        <PinModal
          vehicle={selectedVehicle}
          route={selectedRoute}
          routes={routes}
          onClose={() => setShowPin(false)}
          onSuccess={handleSuccess}
        />
      )}

      {showSwitchDriver && (
        <SwitchDriverModal
          onClose={() => setShowSwitchDriver(false)}
          onSwitched={handleDriverSwitched}
        />
      )}

      {/* Ongoing trip modal (when 15-min skip path detects an active trip) */}
      {ongoingTripSkip && (
        <div className="df-pin-overlay">
          <div className="df-pin-modal-card">
            <p className="df-pin-title df-pin-title--error">Trip Already in Progress</p>
            <p className="df-pin-subtitle df-pin-subtitle--centered">
              You already have an active trip. Complete or cancel it before starting a new one.
            </p>
            <Button className="df-pin-verify-btn" onClick={handleGoToOngoingTrip}>
              GO TO ACTIVE TRIP
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectRoute;
